/**
 * TodayPageError Component
 *
 * Error state component for Today Page.
 * Displays appropriate message based on error type.
 *
 * Atomic level: Template
 */

import React from 'react';
import { RefreshCw, AlertCircle, WifiOff } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';

interface TodayPageErrorProps {
  error: Error | null;
  onRetry: () => void;
}

/**
 * Error state component for Today Page.
 * Displays appropriate message based on error type.
 */
export function TodayPageError({
  error,
  onRetry,
}: TodayPageErrorProps): React.ReactElement {
  // Determine error type
  const isNetworkError =
    error?.message?.includes('network') ||
    error?.message?.includes('fetch') ||
    error?.message?.includes('Failed to fetch');

  const Icon = isNetworkError ? WifiOff : AlertCircle;
  const title = isNetworkError ? 'Connection Lost' : 'Unable to Load Briefing';
  const message = isNetworkError
    ? 'Please check your internet connection and try again.'
    : 'Something went wrong while loading your intelligence briefing.';

  return (
    <div className="flex h-full min-h-0 items-center justify-center overflow-auto bg-[radial-gradient(circle_at_top_left,_rgba(67,66,245,0.08),_transparent_28%),linear-gradient(180deg,_#fbf9f6_0%,_#fffdf8_100%)] p-4 md:p-6">
      <Card className="gap-0 w-full max-w-xl rounded-[28px] border-border/70 bg-background/85 p-0 shadow-[var(--shadow-card)]" padding="sm">
        <CardContent className="p-6 text-center md:p-8">
          <div className="inline-flex items-center justify-center rounded-full bg-destructive/10 p-4 text-destructive">
            <Icon className="h-8 w-8" />
          </div>

          <h1 className="mt-5 font-jakarta text-3xl font-bold tracking-[-0.04em] text-foreground">
            {title}
          </h1>

          <p className="mt-3 text-[13px] leading-[1.7] text-muted-foreground">
            {message}
          </p>

          <Button onClick={onRetry} className="mt-6 gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>

          {process.env.NODE_ENV !== 'production' && error && (
            <pre className="mt-8 max-h-40 overflow-auto rounded-2xl border border-border/70 bg-muted/40 p-4 text-left text-xs text-muted-foreground">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
